import { ApiSystem, Configuration } from "../../state/types";
import { stripUnitSuffix } from "../../utils/systemCode";

const DEFAULT_CLIENT_PROCESS_TEMPLATE = {
      allow_cancel_vehicle: false,
      display_screen_sleep_timer: 60,
      blackout_screen_during_capture: true,
      phone_sync: false,
      phone_uc_upload_delay: false,
      has_undercarriage: false,
      actuator_leg_cycle_time: 20,
      vin_length_err_terminology: "Wrong Identifier Length",
      s3_bucket: "collision-works-d7-dev",
      enabled: {
        0: { name: "WizardVinConfirmation", id: 5 },
        1: { name: "WizardManual", id: 4 },
        2: { name: "WizardExternal", id: 2 },
      },
      location_id: 13,
      cameras: [],
      prefix_removal: 0,
      use_nhtsa_database: false,
      preferences: {
        vinchar: false,
        vinBypass: false,
        skipExternal: false,
        skipPano: false,
        hasLegs: false,
      },
      auto_scan_enabled: false,
      disabled: {
        0: { name: "WizardPano", id: 1 },
        1: { name: "WizardFinal", id: 3 },
        2: { name: "WizardManualPano", id: 6 },
      },
      phone_camera_lens: "wide",
      auto_scan_countdown_length: 3,
      vin_length: 17,
      actuator_fold_trigger_distance: 300,
      unique_id: null,
      // Zero: every leg starts together. The rig moves its legs and doors as one group, and
      // a second between each is a second a vehicle waits in the booth. Raise it only for a
      // supply that cannot take the inrush of starting them all at once.
      actuator_leg_stagger_time: 0,
      overlay: null,
      phone_sync_terminology: "SCAN VIN",
      vin_feedback_terminology: "QA2 Confirmed",
      // 🔹 Default/Display flags depend on how many configs exist
      is_default: false,
      is_display_tablet_rooftop: false,
      manual_scan_countdown_length: 3,
      vin_scan_terminology: "QA2",
      url: "https://collision.3iavi.dev",
      ums_environment: "dev",
      overlays: [],
      name: '',
      phone_camera_resolution: "16:9",
      tablet_rooftop_select: false,
      phone_leg_control: false,
      actuator_trigger_count_threshold: 3,
    };

// Mobile-phone units are standalone systems that talk directly to assetDelivery
// (no odroid, no rig hardware). This template intentionally OMITS every
// odroid-only field (actuators, lighting controller, rig cameras, tablet-rooftop
// display, auto-scan, phone<->odroid sync) and only carries what the mobile
// client actually consumes. Wizards default to the manual capture set
// (Manual + ManualPano) instead of the MQTT-triggered External/Pano captures.
const DEFAULT_MOBILE_CLIENT_PROCESS_TEMPLATE = {
      name: '',
      is_default: false,
      url: "https://collision.3iavi.dev",
      s3_bucket: "collision-works-d7-dev",
      ums_environment: "dev",
      location_id: 13,
      // VIN handling
      vin_length: 17,
      vin_regex: null,
      prefix_removal: 0,
      vin_scan_terminology: "VIN",
      vin_feedback_terminology: "Confirm VIN",
      vin_length_err_terminology: "Wrong Identifier Length",
      regex_rejection_message: null,
      use_nhtsa_database: false,
      // Capture (phone camera)
      phone_camera_lens: "wide",
      phone_camera_lens_alt: "wide",
      phone_camera_resolution: "16:9",
      allow_portrait_manuals: false,
      manual_scan_countdown_length: 3,
      // Workflow / UX
      allow_cancel_vehicle: true,
      preferences: {
        vinchar: false,
        vinBypass: false,
      },
      // Wizards: manual capture set. External/Pano are odroid MQTT-triggered and
      // are omitted entirely for mobile (also hidden in the wizard selector).
      enabled: {
        0: { name: "WizardVinConfirmation", id: 5 },
        1: { name: "WizardManual", id: 4 },
        2: { name: "WizardManualPano", id: 6 },
      },
      disabled: {
        0: { name: "WizardFinal", id: 3 },
      },
      overlays: [],
      overlay: null,
    };

/** True when a system is backed by a standalone mobile phone rather than an
 *  odroid. Absent/undefined unit_type is treated as odroid. */
export const isMobileSystem = (system: { unit_type?: string } | null | undefined): boolean =>
  system?.unit_type === "mobile";

export const findSystemByCode = (systems: any[], code: string | null) => {
  if (!Array.isArray(systems) || !code) return null;
  return systems.find((s: any) => s.systemCode === code) || null;
};

export const buildConfigsList = (system: ApiSystem | null) => {
  if (!system || !system.configurations) return [];
  return Object.entries(system.configurations).map(([configKey, value]) => ({
    configKey,
    name: (value as any)?.clientProcess?.name || configKey,
    ...(value as object),
  }));
};

/**
 * Names that a new config may not take. A config's identity is its **map key**
 * under `configurations`; `clientProcess.name` is only the display name, and
 * `syncConfigName` deliberately freezes the key when a config is renamed. So a
 * config created as `NewConfig_1` and renamed to "Regal Boats" still occupies
 * the key `NewConfig_1` while advertising a different name.
 *
 * Checking names alone therefore misses taken keys: "NewConfig_1" looks free,
 * gets handed to the new config, and `{...configurations, [name]: newConfig}`
 * silently overwrites the renamed one. That wiped a 9-camera config on
 * RGLBTSFLORLDS01 (2026-08-11). Both namespaces have to be excluded.
 */
const takenConfigNames = (configs: any[], existingKeys: string[] = []): Set<string> =>
  new Set<string>([
    ...configs.map((c: any) => c?.clientProcess?.name || ""),
    ...existingKeys,
  ]);

export const getNextUniqueConfigName = (configs: any[], existingKeys: string[] = []): string => {
  const taken = takenConfigNames(configs, existingKeys);

  let index = 1;
  let newName = `NewConfig_${index}`;
  while (taken.has(newName)) {
    index++;
    newName = `NewConfig_${index}`;
  }

  return newName;
};

/**
 * Vets a name the operator typed for a brand-new configuration.
 *
 * The name becomes the map key, and the key is permanent — `syncConfigName`
 * freezes it, so this is the one moment identity is chosen. Naming the config
 * up front is what keeps placeholder keys like `NewConfig_1` out of the data
 * entirely: those are what drift away from the display name and then collide
 * with the next generated name.
 *
 * Dots and brackets are refused because config keys are addressed as document
 * paths elsewhere (`configurations.<key>.clientProcess.name`, and `removePaths`
 * entries like `configurations.<key>`); a dot in the key silently splits the
 * path and points the write somewhere else.
 */
export const validateNewConfigName = (
  rawName: string,
  existingConfigs: any[] = [],
  existingKeys: string[] = []
): { name: string; error?: undefined } | { error: string; name?: undefined } => {
  const name = (rawName || "").trim();

  if (!name) return { error: "Configuration name cannot be empty." };

  if (/[.[\]]/.test(name)) {
    return { error: `Configuration names cannot contain "." "[" or "]". Please rename "${name}".` };
  }

  if (takenConfigNames(existingConfigs, existingKeys).has(name)) {
    return { error: `A configuration named "${name}" already exists.` };
  }

  return { name };
};

export const buildNewConfig = (
  existingConfigs: any[] = [],
  system?: ApiSystem | null,
  requestedName?: string
): any => {
  // Keys come off the system itself, so callers don't have to thread them through.
  // A caller-supplied name wins; the generator is the fallback for callers that
  // don't collect one.
  const newConfigName =
    (requestedName || "").trim() ||
    getNextUniqueConfigName(existingConfigs, Object.keys(system?.configurations || {}))

  const isFirstConfig = existingConfigs.length === 0;

  // Mobile systems get the trimmed template with no odroid-only fields (and no
  // tablet-rooftop flag, which is meaningless without a rig display).
  if (isMobileSystem(system)) {
    return {
      clientProcess: {
        ...DEFAULT_MOBILE_CLIENT_PROCESS_TEMPLATE,
        ums_environment: system?.ums_env || DEFAULT_MOBILE_CLIENT_PROCESS_TEMPLATE.ums_environment,
        is_default: isFirstConfig,
        name: newConfigName,
      },
      assetProcessing: {},
      assetStorage: {},
      assetSyndication: {},
    };
  }

  const newConfig = {
    clientProcess: {
      ...DEFAULT_CLIENT_PROCESS_TEMPLATE,
      ums_environment: system?.ums_env || DEFAULT_CLIENT_PROCESS_TEMPLATE.ums_environment,
      is_default: isFirstConfig,
      is_display_tablet_rooftop: isFirstConfig,
      name: newConfigName
    },
    assetProcessing: {},
    assetStorage: {},
    assetSyndication: {},
  };

  return newConfig;
};

// src/utils/systemHelpers.ts

/**
 * Validates the editable display name while preserving the configuration key
 * as the stable identity for concurrent edits.
 */
const syncConfigName = (system: any, currentConfigName: string): any | null => {
  if (!system?.configurations) return system;
  if (!currentConfigName) return { updated: system, newConfigName: currentConfigName}

  const updated = structuredClone(system);
  const configs = Object.entries(updated.configurations || {}) as [string, any];

  const currentEntry = configs.find(([name]) => name === currentConfigName);
  if (!currentEntry) return updated;

  const [oldKey, currentConfig] = currentEntry;
  const newName = currentConfig?.clientProcess?.name?.trim?.() || oldKey;

  // 🔹 Prevent duplicate names
  const duplicate = configs.some(
    ([name, cfg]) =>
      name !== oldKey &&
      (cfg?.clientProcess?.name?.trim?.() || name) === newName
  );

  if (duplicate) {
    alert(`A configuration named "${newName}" already exists.`);
    return null;
  }

  return { updated, newConfigName: currentConfigName };
};

/**
 * Enforces only one config has clientProcess.is_default = true,
 * and ensures at least one default exists.
 */
const enforceUniqueDefault = (
  system: any,
  currentConfigName: string
): any | null => {
  const updated = structuredClone(system);
  const configs = Object.entries(updated.configurations || {}) as [string, any];
  const current = updated.configurations[currentConfigName];
  const cp = current?.clientProcess || {};

  if (cp.is_default) {
    // turn off all others
    for (const [name, cfg] of configs) {
      if (name !== currentConfigName && cfg?.clientProcess?.is_default) {
        cfg.clientProcess.is_default = false;
      }
    }
  } else {
    const anyDefault = configs.some(
      ([name, cfg]) =>
        name !== currentConfigName && cfg?.clientProcess?.is_default === true
    );
    if (!anyDefault) {
      alert("At least one configuration must be marked as default.");
      return null;
    }
  }

  return updated;
};

/**
 * Enforces only one config has clientProcess.is_display_tablet_rooftop = true,
 * and ensures at least one exists.
 */
const enforceUniqueTabletDefault = (
  system: any,
  currentConfigName: string
): any | null => {
  // Tablet-rooftop is an odroid rig-display concept — mobile systems don't have
  // one, so skip the "at least one tablet-rooftop config" requirement for them.
  if (isMobileSystem(system)) return structuredClone(system);

  const updated = structuredClone(system);
  const configs = Object.entries(updated.configurations || {}) as [string, any];
  const current = updated.configurations[currentConfigName];
  const cp = current?.clientProcess || {};

  if (cp.is_display_tablet_rooftop) {
    // turn off all others
    for (const [name, cfg] of configs) {
      if (
        name !== currentConfigName &&
        cfg?.clientProcess?.is_display_tablet_rooftop
      ) {
        cfg.clientProcess.is_display_tablet_rooftop = false;
      }
    }
  } else {
    const anyTablet = configs.some(
      ([name, cfg]) =>
        name !== currentConfigName &&
        cfg?.clientProcess?.is_display_tablet_rooftop === true
    );
    if (!anyTablet) {
      alert("At least one configuration must be set as display tablet rooftop.");
      return null;
    }
  }

  return updated;
};

/**
 * Master method — orchestrates all name/default validation logic.
 */
export const validateCanSaveSystem = (
  system: any,
  currentConfigName: string
): { updated: any; newConfigName: string } | null => {
  if (!system?.configurations) return { updated: system, newConfigName: currentConfigName };

  // 1️⃣ Sync config name and ensure uniqueness
  const renameResult = syncConfigName(system, currentConfigName);
  if (!renameResult) return null;
  let { updated: systemWithRename, newConfigName } = renameResult;

  // 2️⃣ Enforce only one default
  const defaultResult = enforceUniqueDefault(systemWithRename, newConfigName);
  if (!defaultResult) return null;

  // 3️⃣ Enforce only one tablet default
  const tabletResult = enforceUniqueTabletDefault(defaultResult, newConfigName);
  if (!tabletResult) return null;

  // ✅ Return both the updated system and the final name
  return { updated: tabletResult, newConfigName };
};

export const isDuplicateSystemCode = (currentCode: string,  systems: any[]): boolean => {
  if (!currentCode?.trim()) return false;

  return systems.some(
    (s: any) =>
      s.systemCode.toLowerCase() === currentCode.trim().toLowerCase()
  );
};

export const getUniqueConfigName = (
  existingConfigs: any[],
  baseName: string,
  existingKeys: string[] = []
) => {
  // Keys as well as names — see takenConfigNames for why.
  const taken = takenConfigNames(existingConfigs, existingKeys);

  // match your current naming behavior
  let candidate = baseName.endsWith("_copy") ? `${baseName}_2` : `${baseName}_copy`;

  if (!taken.has(candidate)) return candidate;

  // deterministic-ish uniqueness without Date.now() chaos if you prefer:
  let i = 2;
  while (taken.has(`${candidate}_${i}`)) i++;
  return `${candidate}_${i}`;
};

export interface GroupedSystem {
  label: string;
  systems: { code: string; label: string }[];
}

export const GROUP_ORDER = [
  "Car Dealers",
  "Alliance Auto Autions",
  "America's Auto Actions",
  "Dealers Auto Auctions",
  "Manheim Auto Auctions",
  "Metro Auto Auctions",
  "Boats",
  "RV",
  "Internal",
];

export function groupAndFilterSystems(
  systems: ApiSystem[],
  accessibleSystemCodes: string[],
  searchTerm: string
): GroupedSystem[] {
  const groupMap: Record<string, GroupedSystem> = {};
  const otherGroup: GroupedSystem = { label: "Other", systems: [] };

  GROUP_ORDER.forEach(label => (groupMap[label] = { label, systems: [] }));

  const q = searchTerm.trim().toLowerCase();

  accessibleSystemCodes.forEach(code => {
    const system = systems.find(s => s.systemCode === code);
    if (!system) return;

    const name = system.commonName || "Unknown";
    const systemLabel = `${name} (${code})`;

    const isMatch =
      !q ||
      name.toLowerCase().includes(q) ||
      code.toLowerCase().includes(q);

    if (!isMatch) return;

    const category = system.system_category;
    if (category && groupMap[category]) groupMap[category].systems.push({ code, label: systemLabel });
    else otherGroup.systems.push({ code, label: systemLabel });
  });

  Object.values(groupMap).forEach(g => g.systems.sort((a, b) => a.label.localeCompare(b.label)));
  otherGroup.systems.sort((a, b) => a.label.localeCompare(b.label));

  const all = [...GROUP_ORDER.map(label => groupMap[label]), otherGroup];
  return all.filter(g => g.systems.length > 0);
}

export const normalizeSystemsData = (systems: ApiSystem[]): ApiSystem[] => {
  const byCode = new Map<string, ApiSystem>();

  systems.forEach((system) => {
    const baseSystemCode = stripUnitSuffix(system.systemCode);

    // If there are no configurations, there's nothing to fix.
    const normalizedConfigurations: { [key: string]: Configuration } = {};
    if (system.configurations) {
      // Loop over each configuration by its key (e.g., "umstest", "NewConfig_1")
      for (const configKey in system.configurations) {
        // Standard practice to ensure we're not iterating over prototype properties
        if (Object.prototype.hasOwnProperty.call(system.configurations, configKey)) {
          const originalConfig = system.configurations[configKey];

          // ❗ THE CORE LOGIC: Check if clientProcess.name is missing.
          if (!originalConfig.clientProcess?.name) {
            // If it's missing, create a new config object that includes a
            // clientProcess object with the name set to its key.
            normalizedConfigurations[configKey] = {
              ...originalConfig,
              clientProcess: {
                ...(originalConfig.clientProcess || {...DEFAULT_CLIENT_PROCESS_TEMPLATE}), // Preserve other clientProcess fields if they exist
                name: configKey, // Inject the name!
              },
            };
          } else {
            // If the name exists, the config is fine. Just copy it over.
            normalizedConfigurations[configKey] = originalConfig;
          }
        }
      }
    }

    const normalizedSystem: ApiSystem = {
      ...system,
      systemCode: baseSystemCode,
      configurations: normalizedConfigurations,
    };

    if (!byCode.has(baseSystemCode)) {
      byCode.set(baseSystemCode, normalizedSystem);
    }
  });

  return Array.from(byCode.values());
};
